import React, {useEffect, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import {apiV2} from "@/api/axiosConfig";
import {isAxiosError} from "axios";
import {CreateSiteAlertPage} from "./create-site-alert";
import {Client} from "@/types/userManagement";
import {useDepartment} from "@/api/platform/useDepartments";

type SiteBasedAlert = {
    id: string;
    clientID: string;
    clientName: string;
    name: string;
    locationType: "country" | "facility" | "custom";
    facilityID?: string; // Deprecated - kept for backward compatibility
    facilityName?: string; // Deprecated - kept for backward compatibility
    facilityIDs?: string[];
    facilityNames?: string[];
    notifyFacilityGuardians?: boolean;
    notifyTrackingTeam?: boolean,
    radius?: number;
    countryISOs?: string[];
    countryNames?: string[];
    customShapes?: Array<{
        type: "circle" | "polygon" | "polyline";
        center?: number[]; // [lng, lat]
        radius?: number;
        geometry?: number[][]; // [[lng, lat], ...]
    }>;
    incidentType: "tags" | "categories";
    incidentFeedIDs?: string[];
    incidentCategories?: string[];
    incidentTags?: string[];
    incidentTagLabels?: string[];
    excludedTags?: string[];
    excludedTagLabels?: string[];
    incidentTagMode?: "all" | "any";
    incidentSeverities?: string[];
    alertRecipients?: string[];
    notificationGroupIDs?: string[];
    departmentID?: string;
}

export function EditSiteAlert() {
    const {id} = useParams<{id: string}>();
    const [searchParams] = useSearchParams();
    const prevUrl = searchParams.get("prev") ?? "/site-alerts";
    const [alert, setAlert] = useState<SiteBasedAlert | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const {data: initialDepartment, isLoading: departmentLoading} = useDepartment(alert?.departmentID ?? "");

    useEffect(() => {
        if (!id) {
            setErrorMessage("No alert ID provided");
            setLoading(false);
            return;
        }

        const fetchAlert = async () => {
            try {
                const response = await apiV2.get<SiteBasedAlert>(`site-based-alerts/${id}`);
                setAlert(response.data);
            } catch (e) {
                if (isAxiosError(e) && e?.response?.data?.error) {
                    setErrorMessage(e.response.data.error);
                } else {
                    setErrorMessage("Failed to load alert");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAlert();
    }, [id]);

    if (loading || departmentLoading) {
        return (
            <div className="flex flex-col gap-6 max-w-4xl mx-auto">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p>Loading alert...</p>
                </div>
            </div>
        );
    }

    if (!alert || errorMessage) {
        return (
            <div className="flex flex-col gap-6 max-w-4xl mx-auto">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-dark-red">Failed to load alert</p>
                    {errorMessage && <p className="mt-2">{errorMessage}</p>}
                </div>
            </div>
        );
    }

    // Transform alert data into initial form data
    // Handle both new facilityIDs array and old facilityID single value
    const facilityIDs = alert.facilityIDs && alert.facilityIDs.length > 0
        ? alert.facilityIDs
        : (alert.facilityID ? [alert.facilityID] : []);

    const initialData = {
        name: alert.name,
        locationType: alert.locationType,
        client: alert.clientID ? {
            id: alert.clientID,
            name: alert.clientName,
        } as Client : undefined,
        department: initialDepartment,
        countryCodes: alert.countryISOs || [],
        facilityIDs: facilityIDs,
        radius: alert.radius,
        customShapes: alert.customShapes || [],
        incidentType: alert.incidentType,
        incidentFeedIDs: alert.incidentFeedIDs || [],
        incidentTags: alert.incidentTags && alert.incidentTagLabels
            ? alert.incidentTags.map((slug, i) => [slug, alert.incidentTagLabels![i]] as [string, string])
            : [],
        excludedTags: alert.excludedTags && alert.excludedTagLabels
            ? alert.excludedTags.map((slug, i) => [slug, alert.excludedTagLabels![i]] as [string, string])
            : [],
        tagMode: alert.incidentTagMode,
        categoryTypeIDs: alert.incidentCategories || [],
        incidentSeverities: alert.incidentSeverities || [],
        notifyFacilityGuardians: alert.notifyFacilityGuardians || false,
        alertRecipients: alert.alertRecipients || [],
        notificationGroupIDs: alert.notificationGroupIDs || [],
        notifyTrackingTeam: alert.notifyTrackingTeam
    };

    return <CreateSiteAlertPage alertId={id} initialData={initialData} mode="edit" prevUrl={prevUrl} />;
}
